// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import {Location} from 'react-router';

import {ReportName} from 'common/report/constants';
import {queryApiUrl} from 'common/report/queries/utils';
import {QueryTrackingProps} from 'common/report/util/query-tracking/models/types';
import {apiClient} from 'common/util/api-client';
import {ResponseError} from 'common/util/shared-client';
import {getLocation} from 'common/util/url';

import {QueryReason} from '../util/query-tracking/types';
import {reporter} from '../error-reporting';
import {BaseQueryErrorType, BaseQueryResultsType, ParamsAndResults} from '../types';
import {CurrentProject} from '../model-resources/projects';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {beforeQuery} from './query-lifecycles';

// intended to support insights, funnels, retention, and flows
// this function is currently reused in dashboards to enforce a common querying codepath,
// and may be further consolidated by using QueryManager in dashboards as well
export async function executeReportQuery({
  endpoint: originalEndpoint,
  queryParams,
  overrideHeaders = {},
  abortSignal,
  body: originalBody = {},
  location,
  // dashboards currently only handles errors based on status code
  // however, in reports we may want to use more detailed information provided from the server
  throwOnInvalidStatus = true,
}: {
  endpoint: string;
  queryParams?: Record<string, string>;
  overrideHeaders?: Record<string, string>;
  abortSignal?: AbortSignal;
  body?: any;
  // dashboards currently only handles errors based on status code
  // however, in reports we may want to use more detailed information provided from the server
  throwOnInvalidStatus?: boolean;
  location: Location;
}): Promise<Response> {
  // Run life cycle hooks before making the query request
  // we use this hook to maintain all necessary inline payload transformations
  const {endpoint, urlParams, body} = beforeQuery({
    endpoint: originalEndpoint,
    urlParams: new URLSearchParams(queryParams ?? {}),
    body: cloneDeep(originalBody),
    location,
  });

  const headers: Record<string, string> = {
    ...overrideHeaders,
    'Content-Type': `application/json; charset=UTF-8`,
    'Request-URL': getLocation().href,
    'Bookmark-ID': body.tracking_props?.bookmark_id,
  };

  // using apiClient.fetch because apiClient.post() doesn't support query api format
  // eslint-disable-next-line local-rules/deprecated-member-access
  return apiClient.fetch(queryApiUrl(`${endpoint}?${urlParams.toString()}`), {
    headers,
    method: `POST`,
    body: JSON.stringify(body),
    throwOnInvalidStatus,
    abortSignal,
  });
}

export const queryModule = {executeReportQuery};

export abstract class QueryManager<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType,
> {
  abstract reportName: ReportName;
  abstract endpoint: string;
  protected abstract getExtraPostParams({
    bookmarkParams,
    reportQueryOrigin,
    currentProject,
    location,
  }: {
    bookmarkParams: BookmarkParamsType;
    reportQueryOrigin: BookmarkType;
    currentProject: CurrentProject;
    location: Location;
  });

  public declare projectId: number;
  public declare workspaceId: number;

  private queryController: AbortController = new AbortController();
  private hasQueriedOnce = false;

  constructor({projectId, workspaceId}: {projectId: number; workspaceId?: number}) {
    this.projectId = projectId;
    this.workspaceId = workspaceId;
  }

  protected getEndpoint(_bookmarkParams: BookmarkParamsType): string {
    return this.endpoint;
  }

  // optionally implemented to transform a query error into a report-specific type
  public handleQueryError(err: BaseQueryErrorType): QueryErrorType {
    return err as QueryErrorType;
  }

  // not all reports support csv export, so this is optionally implemented
  public getExtraCsvPostParams(_params: {
    bookmarkParams: BookmarkParamsType;
    reportQueryOrigin: BookmarkType;
    currentProject: CurrentProject;
    limit: number;
  }) {
    return {};
  }

  private get queryParams() {
    return {
      ...(this.workspaceId ? {workspace_id: this.workspaceId.toString()} : {}),
      project_id: this.projectId.toString(),
    };
  }

  public async csvExportQuery({
    bookmarkParams,
    reportQueryOrigin,
    currentProject,
    location,
    useQueryCache = true,
    limit,
  }: {
    bookmarkParams: BookmarkParamsType;
    reportQueryOrigin: BookmarkType;
    currentProject: CurrentProject;
    location: Location;
    useQueryCache?: boolean;
    limit: number;
  }): Promise<{
    result: string;
    filename?: string;
    isSegmentationLimitHit?: boolean;
  }> {
    const postPayload = {
      bookmark: bookmarkParams,
      use_query_cache: useQueryCache,
      ...this.getExtraCsvPostParams({bookmarkParams, reportQueryOrigin, currentProject, limit}),
      format: `csv`,
    };

    // no need to abort pending csv queries
    const result = await queryModule.executeReportQuery({
      endpoint: this.getEndpoint(bookmarkParams),
      queryParams: this.queryParams,
      body: postPayload,
      abortSignal: null,
      throwOnInvalidStatus: true,
      location,
    });
    const csvData = await result.text();
    const filename = result.headers.get(`Content-Disposition`)?.match(/filename="([^"]+)"/)?.[1];
    return {
      result: csvData,
      filename,
      ...(result.headers.get(`X-IS-SEGMENTATION-LIMIT-HIT`) && {
        isSegmentationLimitHit: result.headers.get(`X-IS-SEGMENTATION-LIMIT-HIT`) === `1`,
      }),
    };
  }

  public abortPendingQuery() {
    this.queryController.abort();
    this.queryController = new AbortController();
  }

  public async query({
    bookmarkParams,
    reportQueryOrigin,
    currentProject,
    queryReason,
    hasUnsavedBookmarkChanges,
    useQuerySampling,
    location,
    bookmarkId,
    useQueryCache = true,
  }: {
    bookmarkParams: BookmarkParamsType;
    reportQueryOrigin: BookmarkType;
    currentProject: CurrentProject;
    queryReason: QueryReason;
    hasUnsavedBookmarkChanges: boolean;
    useQuerySampling: boolean;
    location: Location;
    bookmarkId?: null | number;
    useQueryCache?: boolean;
  }): Promise<QueryResultsType | QueryErrorType> {
    this.abortPendingQuery();

    const trackingProps: QueryTrackingProps = {
      is_main_query_for_report: true,
      report_name: this.reportName,
      bookmark_id: bookmarkId,
      has_unsaved_changes: hasUnsavedBookmarkChanges,
      query_reason: queryReason,
      request_url: getLocation().href,
    };

    const postPayload = {
      tracking_props: trackingProps,
      bookmark: bookmarkParams,
      ...this.getExtraPostParams({
        bookmarkParams,
        reportQueryOrigin,
        location,
        currentProject,
      }),
      use_query_cache: useQueryCache,
      use_query_sampling: useQuerySampling,
    };

    try {
      const response = await queryModule.executeReportQuery({
        endpoint: this.getEndpoint(bookmarkParams),
        queryParams: this.queryParams,
        body: postPayload,
        abortSignal: this.queryController.signal,
        throwOnInvalidStatus: false,
        location,
      });

      const results = await response.json();

      // query-api responded with a 4XX - get any detailed information from the response
      if (results.error) {
        const error: BaseQueryErrorType = {
          status: response.status,
          error: results.error,
          data: results.data,
          ...results,
        };

        return this.handleQueryError(error);
      }

      // TODO: run handleResults here when QueryManager is used for query promises passed into ReportEditor
      return results;
    } catch (err) {
      if (err instanceof ResponseError) {
        // apiClient will only throw here if response.status >=500
        return this.handleQueryError({error: err.getMessage(), status: err.response.status});
      } else if (err.name === `AbortError`) {
        return null;
      }
      const error = err as Error;
      reporter.error(`Error processing ${this.getEndpoint(bookmarkParams)} query`, err);
      return this.handleQueryError({error: error.stack, status: null});
    } finally {
      this.hasQueriedOnce = true;
    }
  }

  /**
   * Optional method to override and transform bookmark params or results when query is returned.
   * Sometimes we need to change bookmark settings based on results and vice-versa
   * @param paramsAndResults - current bookmark params and results returned from query
   * @returns params and results, optionally modified
   */
  handleResults(
    paramsAndResults: ParamsAndResults<BookmarkParamsType, QueryResultsType>,
  ): ParamsAndResults<BookmarkParamsType, QueryResultsType> {
    return paramsAndResults;
  }

  /**
   * Used to determine if the query has been run at least once.
   */
  hasQueried(): boolean {
    return this.hasQueriedOnce;
  }
}
